import Model from '@ember-data/model';
import { isEmpty } from '@ember/utils';
import { computed } from '@ember/object';
import { or, equal, notEmpty } from '@ember/object/computed';
import attr from 'ember-data/attr';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import EnvironmentService from 'cio/services/environment';
import { FormType, RESERVED_FORM_FIELD_NAMES, SharedFormModelInterface } from 'cio/utils/forms';
import { IdentifierKey } from 'cio/utils/identifiers';

export enum CustomFormStatus {
  Draft = 'draft',
  Connected = 'connected',
  Disconnected = 'disconnected',
}

export const ErrKeyMissingIdentifier = 'missing_identifier';
export const ErrKeyMultipleIdentifiers = 'multiple_identifiers';
export const ErrKeyInvalidEmailIdentifier = 'invalid_email_identifier';

type CustomFormErrorType =
  | typeof ErrKeyMissingIdentifier
  | typeof ErrKeyMultipleIdentifiers
  | typeof ErrKeyInvalidEmailIdentifier
  | '';

export interface CustomFormAttrMap {
  formFieldKey: string;
  formFieldLabel: string;
  formFieldType: Exclude<HTMLInputElement['type'], 'submit' | 'button' | 'image' | 'password'> | 'select' | 'textarea';
  cioAttr: string;
}

class CustomForm extends Model implements SharedFormModelInterface {
  @service store!: DS.Store;
  @service environment!: EnvironmentService;

  // always set to RecordArray with all segments currently in Ember Data store. Used to compute "formSegment":
  private allSegments = this.store.peekAll('segment');

  @attr('string') name!: string;
  @attr('string') formId!: string;
  @attr('string', { defaultValue: () => FormType.Custom }) formType!: Exclude<FormType, FormType.FacebookLeadAds>;
  @attr('string') error!: CustomFormErrorType;
  @attr('number') createdAt!: number;
  @attr('number') updatedAt!: number;

  // html fields, needed for create POST request:
  // required by back-end for internal use, generating form_id
  @attr('string') htmlFormId!: string;
  @attr('string') htmlFormName!: string;
  @attr('string') htmlFormUrl!: string;
  @attr({ defaultValue: () => [] }) htmlFormInputs!: string[];

  // generated by back-end on save of scanned forms:
  @attr('string') htmlFingerprint!: string;

  @attr('string', { defaultValue: CustomFormStatus.Draft }) status!: CustomFormStatus;
  @attr('number') lastActivityAt!: number;
  @attr('string') apiFormId!: string;
  @attr('camel-case', { defaultValue: () => [] }) attrMaps!: CustomFormAttrMap[];

  updateAttrMapAtIndex(index: number, attrMap: CustomFormAttrMap) {
    let attrMaps = [...this.attrMaps];
    attrMaps[index] = attrMap;
    this.attrMaps = attrMaps;
  }

  @computed('formType', 'apiFormId')
  get isApiForm() {
    return this.formType === FormType.Custom && !isEmpty(this.apiFormId);
  }
  @computed('formType', 'isApiForm')
  get isScannedForm() {
    return this.formType === FormType.Custom && !isEmpty(this.htmlFormInputs);
  }
  @computed('formType', 'isApiForm', 'isScannedForm')
  get isHTMLActionForm() {
    return this.formType === FormType.Custom && !this.isApiForm && !this.isScannedForm;
  }
  @computed('formType')
  get isTypeform() {
    return this.formType === FormType.Typeform;
  }
  @computed('formType')
  get isJotform() {
    return this.formType === FormType.Jotform;
  }

  @equal('status', CustomFormStatus.Draft) isDraft!: boolean;
  @equal('status', CustomFormStatus.Connected) isConnected!: boolean;
  @equal('status', CustomFormStatus.Disconnected) isDisconnected!: boolean;
  @or('isConnected', 'isDraft') isConnectedOrDraft!: boolean;

  @notEmpty('error') hasError!: boolean;
  @equal('error', ErrKeyMissingIdentifier) hasErrMissingIdentifier!: boolean;
  @equal('error', ErrKeyMultipleIdentifiers) hasErrMultipleIdentifiers!: boolean;
  @equal('error', ErrKeyInvalidEmailIdentifier) hasErrInvalidEmailIdentifier!: boolean;

  get value() {
    return this.formId;
  }

  get label() {
    return this.name;
  }

  @computed('formId')
  get formIdLowercase() {
    return this.formId.toLowerCase();
  }

  get isNewForm() {
    let { formType, apiFormId, createdAt, updatedAt } = this;

    // form settings were already updated: it's never marked as "new" in UI
    if (updatedAt !== createdAt) {
      return false;
    }

    return (formType === FormType.Custom && apiFormId !== '') || formType === FormType.Typeform;
  }

  get hasDuplicateFieldKey() {
    let keys = this.attrMaps.map((f) => f.formFieldKey.trim()).filter((k) => k); // filter out empty strings

    return new Set(keys).size !== keys.length;
  }

  get hasDuplicateFieldMapping() {
    let attrNames = this.attrMaps.map((f) => f.cioAttr.trim()).filter(Boolean); // filter out empty strings
    return new Set(attrNames).size !== attrNames.length;
  }

  get hasEmptyFieldMapping() {
    return this.attrMaps.toArray().some((f) => f.formFieldKey.trim() === '' || f.cioAttr.trim() === '');
  }

  get hasReservedFieldMapping() {
    return this.attrMaps.toArray().some((f) => RESERVED_FORM_FIELD_NAMES.includes(f.formFieldKey.trim()));
  }

  get hasCioIdFieldMapping() {
    return this.attrMaps.toArray().some((f) => f.cioAttr === IdentifierKey.CioId);
  }

  get identifierAttrMaps() {
    let identifiers = this.environment.current.enabledIdentifiers;
    return this.attrMaps.filter((am) => identifiers.find((id) => id === am.cioAttr));
  }

  // Depends on workspace segments being loaded into the Ember Data store:
  @computed('formId', 'allSegments.[]')
  get formSegment() {
    return this.allSegments.findBy('linkedFormId', this.formId);
  }

  @computed('formType', 'formId')
  get typeformManageUrl() {
    if (this.formType !== FormType.Typeform) {
      return;
    }

    return `https://admin.typeform.com/form/${this.formId}/create`;
  }

  @computed('formType', 'formId')
  get jotformManageUrl() {
    if (this.formType !== FormType.Jotform) {
      return;
    }

    return `https://jotform.com/build/${this.formId}`;
  }
}

export default CustomForm;

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'custom-form': CustomForm;
  }
}
